// Configuration des catégories d'opportunités : liste de libellés (cards)

import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Popconfirm,
  Typography,
  Modal,
  Form,
  Input,
  Divider,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PartitionOutlined } from '@ant-design/icons';
import { useAppTranslation } from '../../../hooks/useAppTranslation';

const { Text } = Typography;

interface OpportunityCategoriesStepProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  showDivider?: boolean;
  description?: string;
  /** Désactive les actions pendant une écriture Firestore. */
  saving?: boolean;
}

const OpportunityCategoriesStep: React.FC<OpportunityCategoriesStepProps> = ({
  value = [],
  onChange,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
  showDivider = true,
  description,
  saving = false,
}) => {
  const { t } = useAppTranslation(['activity']);
  const [categories, setCategories] = useState<string[]>(value);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form] = Form.useForm<{ label: string }>();

  useEffect(() => {
    setCategories(value ?? []);
  }, [value]);

  const handleChange = (newList: string[]) => {
    setCategories(newList);
    onChange?.(newList);
  };

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    form.setFieldsValue({ label: '' });
    setModalOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    form.setFieldsValue({ label: categories[index] });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const label = (values.label as string)?.trim();
      if (!label) return;

      const duplicate = categories.some(
        (existing, i) =>
          existing.trim().toLowerCase() === label.toLowerCase() && i !== editingIndex
      );
      if (duplicate) {
        message.warning(t('opportunityCategories.duplicate'));
        return;
      }

      let newList: string[];
      if (editingIndex !== null) {
        newList = categories.map((s, i) => (i === editingIndex ? label : s));
      } else {
        newList = [...categories, label];
      }
      handleChange(newList);
      setModalOpen(false);
      setEditingIndex(null);
      form.resetFields();
    } catch {
      // validation Ant Form
    }
  };

  const handleDelete = (index: number) => {
    const newList = categories.filter((_, i) => i !== index);
    handleChange(newList);
  };

  const showActions = canUpdate || canDelete;
  const isEditing = editingIndex !== null;

  return (
    <>
      {showDivider && <Divider />}
      <Card
        size="small"
        title={
          <Space>
            <PartitionOutlined />
            <span>{t('opportunityCategories.cardTitle')}</span>
          </Space>
        }
        extra={
          canCreate ? (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={saving}
            >
              {t('opportunityCategories.add')}
            </Button>
          ) : null
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {description ?? t('opportunityCategories.subtitle')}
        </Text>
        {categories.length === 0 ? (
          <Text type="secondary">{t('opportunityCategories.empty')}</Text>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {categories.map((label, index) => (
              <Card key={`${label}-${index}`} size="small" styles={{ body: { padding: '10px 12px' } }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Text strong>{label}</Text>
                  {showActions && (
                    <Space>
                      {canUpdate && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(index)}
                          disabled={saving}
                        />
                      )}
                      {canDelete && (
                        <Popconfirm
                          title={t('opportunityCategories.deleteConfirm')}
                          onConfirm={() => handleDelete(index)}
                          okText={t('opportunityCategories.delete')}
                          cancelText={t('opportunityCategories.cancel')}
                          disabled={saving}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={saving}
                          />
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Card>
            ))}
          </Space>
        )}
      </Card>

      <Modal
        title={
          isEditing
            ? t('opportunityCategories.modalEditTitle')
            : t('opportunityCategories.modalAddTitle')
        }
        open={modalOpen}
        onOk={handleSave}
        confirmLoading={saving}
        onCancel={() => {
          setModalOpen(false);
          setEditingIndex(null);
          form.resetFields();
        }}
        okText={
          isEditing ? t('opportunityCategories.edit') : t('opportunityCategories.add')
        }
        cancelText={t('opportunityCategories.cancel')}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="label"
            label={t('opportunityCategories.fieldLabel')}
            rules={[
              { required: true, message: t('opportunityCategories.fieldRequired') },
              { max: 80, message: t('opportunityCategories.fieldMax') },
            ]}
          >
            <Input placeholder={t('opportunityCategories.inputPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default OpportunityCategoriesStep;
